<!--
// app/components/layout/header/LangSwitcher.vue
-->
<script setup lang="ts">
import type { Locale } from "@intlify/core-base";
import { useLocalesStore } from "~/app/stores/localesStore";
import { SwitchLocalePathLink } from "#components";
import nuxtLink from "#app/components/nuxt-link";

const { locale, locales } = useI18n();
const error = useError();
const switchLocalePath = useSwitchLocalePath();
const router = useRouter();
const route = useRoute();
const localesStore = useLocalesStore();

const availableLocales = computed(() => {
  return locales.value.filter(i => i.code !== locale.value);
});

const isNewsPage = computed(() => {
  return route.params.news && !route.params.post;
});

const cleanNewsPath = (lang: Locale): string => {
  const isPaginatedPage = isNewsPage && route.query.page;

  if (isPaginatedPage) {
    return switchLocalePath(lang).split("?page=")[0];
  } else {
    return switchLocalePath(lang);
  }
};

type Props = {
  dark?: boolean;
  buttonId: string;
  dropdownToggle: string;
};

defineProps<Props>();

const dropdownPanel = ref<HTMLElement | null>(null);
</script>

<template>
  <button
    v-if="!error || error.statusCode !== 404"
    :id="buttonId"
    :data-dropdown-toggle="dropdownToggle"
    data-dropdown-offset-skidding="0"
    :class="[
      'font-semibold text-sm py-1 text-center flex items-center',
      dark ? 'text-gray-400' : 'text-gray-500',
    ]"
    type="button"
  >
    {{ locale }}
    <svg
      class="w-2 h-2 ml-1 rtl:mr-1 rtl:ml-0"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 10 6"
    >
      <path
        stroke="currentColor"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="m1 1 4 4 4-4"
      />
    </svg>
  </button>

  <!-- Dropdown menu -->
  <div
    v-if="!error || error.statusCode !== 404"
    ref="dropdownPanel"
    :id="dropdownToggle"
    :class="['z-30 hidden divide-y divide-gray-100 rounded-lg shadow', dark ? 'bg-zinc-900' : 'bg-white']"
  >
    <!--News switcher-->
    <ul v-if="isNewsPage" class="text-sm font-semibold">
      <li v-for="avLocale in availableLocales">
        <component
          :is="
            localesStore.currentRegionAvailableNewsLocales &&
            localesStore.currentRegionAvailableNewsLocales.includes(avLocale.code)
              ? nuxtLink
              : 'span'
          "
          :class="[
            'switcher-link-news block px-4 py-2 cursor-pointer uppercase',
            dark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black',
            {
              '!text-gray-300 !cursor-default hover:!text-gray-300 line-through':
                !localesStore.currentRegionAvailableNewsLocales?.includes(avLocale.code),
            },
          ]"
          :to="cleanNewsPath(avLocale.code)"
          @click="dropdownPanel?.classList.add('hidden')"
          no-prefetch
        >
          {{ avLocale.code }}
        </component>
      </li>
    </ul>

    <!--Generic switcher-->
    <ul v-else :class="['text-sm font-semibold ']" :aria-labelledby="buttonId">
      <li v-for="avLocale in availableLocales" :key="avLocale.code">
        <switch-locale-path-link
          :locale="avLocale.code"
          :class="[
            'switcher-link block px-4 py-2 cursor-pointer uppercase',
            dark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black',
            {
              '!text-gray-300 !cursor-default hover:!text-gray-300 line-through': !switchLocalePath(
                avLocale.code
              ),
            },
          ]"
          @click="dropdownPanel?.classList.add('hidden')"
          no-prefetch
          >{{ avLocale.code }}</switch-locale-path-link
        >
      </li>
    </ul>
  </div>
</template>

<style scoped></style>
